import { useState } from 'react';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { ArrowLeft, Mail, KeyRound, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export default function ForgotPassword() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess('E-mail de recuperação enviado! Verifique sua caixa de entrada.');
      setStep(2);
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || 'Erro ao enviar e-mail de recuperação'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess('Senha redefinida com sucesso! Redirecionando...');
      setTimeout(() => {
        setLocation('/login');
      }, 2000);
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || 'Erro ao redefinir senha. Verifique o token.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/login')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Recuperar Senha</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 space-y-4">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-3 py-2">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
              step === 1
                ? 'bg-primary text-white'
                : 'bg-primary/20 text-primary'
            }`}
          >
            1
          </div>
          <div className="w-12 h-0.5 bg-gray-300" />
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
              step === 2
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-400'
            }`}
          >
            2
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Success Banner */}
        {success && (
          <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        )}

        {/* Step 1: Request Reset */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-gray-900">Informe seu e-mail</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Enviaremos um código de recuperação para o e-mail cadastrado.
              </p>
            </div>

            <form onSubmit={handleRequestReset} className="p-4 space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  E-mail
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  disabled={isLoading}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder:text-gray-400
                    focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20
                    disabled:opacity-50 disabled:bg-gray-50 transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold
                  disabled:opacity-50 active:bg-primary/90 transition-all"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar Código'
                )}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Reset Password */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-gray-900">Redefinir Senha</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Insira o código recebido por e-mail e sua nova senha.
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="p-4 space-y-4">
              <div className="space-y-2">
                <label htmlFor="token" className="text-sm font-medium text-gray-700">
                  Código de Recuperação
                </label>
                <input
                  id="token"
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Insira o código recebido"
                  required
                  disabled={isLoading}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder:text-gray-400
                    focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20
                    disabled:opacity-50 disabled:bg-gray-50 transition-all"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Nova Senha
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  disabled={isLoading}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder:text-gray-400
                    focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20
                    disabled:opacity-50 disabled:bg-gray-50 transition-all"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Confirmar Senha
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a nova senha"
                  required
                  disabled={isLoading}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder:text-gray-400
                    focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20
                    disabled:opacity-50 disabled:bg-gray-50 transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold
                  disabled:opacity-50 active:bg-primary/90 transition-all"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Redefinindo...
                  </>
                ) : (
                  'Redefinir Senha'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setError(null);
                  setSuccess(null);
                }}
                className="w-full text-center text-sm text-primary font-medium py-2"
              >
                Reenviar código
              </button>
            </form>
          </div>
        )}

        {/* Back to login link */}
        <div className="text-center pt-2">
          <button
            onClick={() => setLocation('/login')}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Voltar para o login
          </button>
        </div>
      </main>
    </div>
  );
}
